import { createApp, type App } from 'vue';
import AntDesignVue from 'ant-design-vue';
import ResidentReportDrawer from './index.vue';
import {
  getReportEditPayloadFromPage,
  type ReportEditPayload,
} from './report-edit-payload';

const MOUNT_ID = 'residentReportDrawerApp';
type ResidentReportDrawerMode = 'create' | 'edit';

let app: App<Element> | null = null;
type ResidentReportDrawerInstance = {
  open: (mode?: ResidentReportDrawerMode, payload?: ReportEditPayload) => void;
  requestDelete: (reportId: number) => void;
};

let instance: ResidentReportDrawerInstance | null = null;
let currentMountEl: HTMLElement | null = null;
let triggersBound = false;

function resolveMountElement(root?: ParentNode): HTMLElement | null {
  const mountEl = root
    ? (root.querySelector<HTMLElement>(`#${MOUNT_ID}`) ?? document.getElementById(MOUNT_ID))
    : document.getElementById(MOUNT_ID);

  if (mountEl) {
    return mountEl;
  }

  const dynamicMountEl = document.createElement('div');
  dynamicMountEl.id = MOUNT_ID;
  document.body.appendChild(dynamicMountEl);

  return dynamicMountEl;
}

function mountResidentReportDrawer(root?: ParentNode): void {
  const mountEl = resolveMountElement(root);
  if (!mountEl) return;

  if (app && currentMountEl) {
    app.unmount();
    currentMountEl.innerHTML = '';
  }

  app = createApp(ResidentReportDrawer);
  app.use(AntDesignVue);
  instance = app.mount(mountEl) as unknown as ResidentReportDrawerInstance;
  currentMountEl = mountEl;
}

export function openResidentReportDrawer(
  mode: ResidentReportDrawerMode = 'create',
  payload?: ReportEditPayload,
): void {
  if (!app || !instance) {
    mountResidentReportDrawer();
  }

  instance?.open(mode, payload);
}

export function requestDeleteReport(reportId: number): void {
  if (!app || !instance) {
    mountResidentReportDrawer();
  }

  instance?.requestDelete(reportId);
}

export function initResidentReportDrawer(root?: ParentNode): void {
  mountResidentReportDrawer(root);

  if (triggersBound) return;
  triggersBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const trigger = target?.closest<HTMLElement>('[data-report-create-open-drawer]');

    if (!trigger) return;

    event.preventDefault();

    const mode: ResidentReportDrawerMode =
      trigger.dataset.reportDrawerMode === 'edit' ? 'edit' : 'create';

    if (mode === 'edit') {
      const payload = getReportEditPayloadFromPage();
      if (!payload) return;
      openResidentReportDrawer('edit', payload);
      return;
    }

    openResidentReportDrawer('create');
  });
}
